import { Box } from '@mui/material'
import { useDigitalTwin, useDigitalTwinSettings } from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { DEVIATION_SETTING_PRIORITIES } from 'api/digitalTwin/digitalTwinConstants'
import { useGetAllEventMutation } from 'api/events/events.api'
import { postStat } from 'api/stats/stats.api'
import { formatEndTime, formatStartTime } from 'helpers/dateTime.helper/dateTime.helper'
import {
  getDigitalTwinTagsFromDataId,
  matchesSomeTagFromEveryTopLevel,
} from 'helpers/filterComponent.helper/filterComponent.helper'
import { getDigitalTwinSettingValueString } from 'helpers/optimizeSettings.helper/optimizeSettings.helper'
import { getRoute } from 'helpers/route.helper/route.helper'
import { getDeviationSettingPriorities } from 'helpers/settingPriority.helper/settingPriority.helper'
import { useActiveTags } from 'hooks/filterHooks/filterHooks'
import moment from 'moment'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import filterStore, { getTopLevelTagsFromTags } from 'store/filter/filter'
import SearchField from 'ui/atoms/SearchField/SearchField'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'
import {
  getFilteredPropertiesByPriority,
  getPropertiesFromDigitalTwinModel,
} from 'utils/digitalTwinSettings/digitalTwinSettingUtils'
import { useSnapshot } from 'valtio'
import { getPrettyName, getPropertyPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import EventPostViewModal from 'views/EventsView/components/EventPostViewModal/EventPostViewModal'
import { EventPost } from 'views/EventsView/components/events.helper'
import optimizeViewStore from 'views/OptimizeView/store/optimizeViewStore'
import DeviationSettingsListItem from './DeviationSettingsListItem'
import styles from './DeviationSettingsMenu.module.less'
import DeviationSettingsMenuTab from './DeviationSettingsMenuTab'
import { getGroupedSettings, getGroupedSettingsMatchingSearchTerm } from './DeviationSettingsMenuUtils'

type DeviationSettingsMenuProps = {
  menuWidth: number
  headerHeight: number
  onToggle: (isOpen: boolean) => void
}

export default function DeviationSettingsMenu({
  menuWidth,
  headerHeight,
  onToggle,
}: DeviationSettingsMenuProps): ReactElement {
  const { t } = useTranslation()
  const { activeSystem } = useAuth()
  const optimizeViewSnap = useSnapshot(optimizeViewStore)
  const { filterComponentPerRoute } = useSnapshot(filterStore)
  const language = localStorage.getItem('language')
  const route = getRoute()

  const systemPrimaryDigitalTwin = activeSystem?.primary_digital_twin
  const { data: digitalTwin } = useDigitalTwin(systemPrimaryDigitalTwin?.uid)

  const { mutateAsync: fetchDigitalTwinSettings, isPending: isPendingSettings } = useDigitalTwinSettings()

  //The link button filters DeviationSettings on the component-tag, the button won't work if the tag doesn't exist
  const showDeviationSettingsLinkButton = useMemo(() => {
    //Should be possible to change to getTopLevelTagsForRoute if DigitalTwinSettingsView uses FilterComponent
    return digitalTwin?.model.all_tags?.some((tag) => tag.includes('component/')) ?? false
  }, [digitalTwin?.model.all_tags])

  const [digitalTwinSettings, setDigitalTwinSettings] = useState<DigitalTwinSetting[]>([])
  const [operationalEvents, setOperationalEvents] = useState<Map<number, EventPost>>(new Map<number, EventPost>())
  const [searchTerm, setSearchTerm] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [eventPostData, setEventPostData] = useState<EventPost | undefined>(undefined)

  const hasFilterComponent = filterComponentPerRoute[route]
  const startTime = optimizeViewSnap.optimizedViewRange[0]
  const endTime = optimizeViewSnap.optimizedViewRange[1]

  useEffect(() => {
    if (digitalTwin?.uid !== undefined) {
      fetchDigitalTwinSettings({
        digitalTwinUid: digitalTwin?.uid,
        activeFrom: startTime,
        activeTo: endTime,
        priorities: getDeviationSettingPriorities(activeSystem?.setting_priority_levels),
      }).then((settings) => {
        setDigitalTwinSettings(settings)
      })
    }
  }, [activeSystem?.setting_priority_levels, digitalTwin?.uid, endTime, fetchDigitalTwinSettings, startTime])

  const { mutateAsync: fetchOperationalEvents } = useGetAllEventMutation()

  const { tagsPerUiconfig } = useSnapshot(filterStore)
  const { activeTags } = useActiveTags({ availableFiltersSubset: tagsPerUiconfig })
  const currentTags = useMemo(() => (activeTags ? Array.from(activeTags) : []), [activeTags])
  const topLevelTags = getTopLevelTagsFromTags(currentTags)

  const refetchOperationalEvents = useCallback(() => {
    const ids = Array.from(
      new Set(
        digitalTwinSettings.map((setting) => setting.operational_event).filter((id) => id !== undefined && id !== null)
      )
    )
    if (ids.length === 0) {
      return
    }

    fetchOperationalEvents({
      ids: ids,
    }).then((data) => {
      const dict = new Map<number, EventPost>()
      data.forEach((event) => {
        if (event.id !== undefined) {
          dict.set(event.id, event)
        }
      })
      setOperationalEvents(dict)
    })
  }, [fetchOperationalEvents, digitalTwinSettings])

  useEffect(() => {
    if (isMenuOpen && digitalTwinSettings.length > 0) {
      refetchOperationalEvents()
    }
  }, [digitalTwinSettings, isMenuOpen, refetchOperationalEvents])

  const handleMenuToggle = useCallback(
    (isOpen: boolean) => {
      postStat('deviation_settings_menu', `menu-${isOpen ? 'opens' : 'closes'}`) //DATA LOG FOR PILOT 1,5
      setIsMenuOpen(isOpen)
      onToggle(isOpen)
    },
    [onToggle]
  )

  const activeSettingsAndNoSettings = useMemo(() => {
    //get all active settings
    const activeSettingsByName = getGroupedSettings(digitalTwinSettings, language, digitalTwin?.translations)
    //get all properties (including the ones with active settings)
    const allProperties = digitalTwin?.model
      ? getPropertiesFromDigitalTwinModel(digitalTwin.name, digitalTwin.model)
      : []

    //filter out properties depending on if they have tags and if they are deviation settings
    const filteredProperties = getFilteredPropertiesByPriority(
      allProperties.filter((property) => property?.tags?.length),
      DEVIATION_SETTING_PRIORITIES
    )

    //map filtered properties to stringarray (we are only interested in the name and tags of the property)
    {
    }
    const filteredPropertiesNamesAndTags = filteredProperties[0].map((property) => ({
      name: property.name,
      tags: property.tags?.filter((tag) => {
        for (const topLevelTag of topLevelTags) {
          if (tag.includes(topLevelTag)) return true
        }
        return false
      }), //excluding tags that doesn't exist in production plan
    }))

    const activeSettingsMap = new Map(activeSettingsByName.map(([name, settings]) => [name, settings]))
    const uniquePropertiesNames = new Set(filteredPropertiesNamesAndTags.map((property) => property.name))

    //add the names of properties without any active deviation settings
    const propertiesWithoutSettingsByName = Array.from(uniquePropertiesNames)
      .filter((propertyName) => propertyName && !activeSettingsMap.has(propertyName))
      .map((propertyName) => [propertyName, []])

    const resultWithTags = [...activeSettingsByName, ...propertiesWithoutSettingsByName].map((setting) => {
      const matchedProperty = filteredPropertiesNamesAndTags.find((property) => property.name === setting[0])
      return {
        name: setting[0],
        settings: setting[1] as DigitalTwinSetting[],
        tags: matchedProperty ? matchedProperty.tags : [],
      }
    })

    return resultWithTags
  }, [digitalTwinSettings, language, digitalTwin?.translations, digitalTwin?.model, digitalTwin?.name, topLevelTags])

  const filteredSettings: [string, DigitalTwinSetting[]][] = useMemo(() => {
    const hiddenTags = digitalTwin?.model?.hidden_default_tags ?? []
    const hiddenTopTags = hiddenTags.filter((tag) => !tag.includes('/'))
    return activeSettingsAndNoSettings
      .filter(({ tags }) => {
        // Remove tags that are hidden by top level tags
        // Example: remove 'permission/ratio' from tags when hidden_default_tags only includes 'permission'
        const filteredTags = tags?.filter((tag) => !hiddenTopTags.includes(tag.split('/')[0]))

        return filteredTags?.length && currentTags && matchesSomeTagFromEveryTopLevel(filteredTags, currentTags)
      })
      .map(({ name, settings }) => [name as string, settings])
  }, [activeSettingsAndNoSettings, currentTags, digitalTwin?.model?.hidden_default_tags])

  const displayedSettings: [string, DigitalTwinSetting[]][] = useMemo(() => {
    return hasFilterComponent
      ? filteredSettings
      : activeSettingsAndNoSettings.map(({ name, settings }) => [name as string, settings])
  }, [filteredSettings, hasFilterComponent, activeSettingsAndNoSettings])

  const activeSettingsAndNoSettingsMatchingSearchTerm = useMemo(
    () => getGroupedSettingsMatchingSearchTerm(searchTerm, displayedSettings, digitalTwin?.translations),
    [searchTerm, displayedSettings, digitalTwin?.translations]
  )
  const propertiesWithActiveSettingsAndWithoutEntries =
    searchTerm.length > 0 ? activeSettingsAndNoSettingsMatchingSearchTerm : displayedSettings

  const totalDigitalTwinSettingsCount = useMemo(() => {
    return displayedSettings.reduce((acc, [_, settings]) => acc + settings.length, 0)
  }, [displayedSettings])

  const allTagsDict = useMemo(() => digitalTwin?.model?.all_tags_dict || {}, [digitalTwin?.model])

  const settingsListItem = useMemo(() => {
    return propertiesWithActiveSettingsAndWithoutEntries.map(([key, settings], index) => {
      const tagsFromDigitalTwin = getDigitalTwinTagsFromDataId(key, allTagsDict) ?? []
      const componentTag = tagsFromDigitalTwin.find((p) => p.includes('component/')) ?? ''
      const componentTagName = componentTag.split('component/')[1] ?? ''

      const targetObjectName = getPrettyName(key, digitalTwin?.translations)
      const transformedSettings = settings.map((setting) => ({
        id: setting.id,
        name: getPropertyPrettyName(setting.name, setting.attribute, digitalTwin?.translations),
        value: getDigitalTwinSettingValueString({
          settingStartTime: setting.start_time ?? Datetime.toISOString(moment().startOf('day')),
          settingEndTime: setting.end_time ? Datetime.toISOString(moment(setting.end_time)) : null,
          activeValue: setting.value,
          periodStartTime: startTime,
          periodEndTime: endTime,
          attribute: setting.attribute,
          baseValue: null,
          isTimeSeries: setting.isTimeSeries,
        }),
        startDate: formatStartTime(setting.start_time?.toString() ?? ''),
        endDate: formatEndTime(setting.end_time ? Datetime.ISODatetimeTo59Minutes(setting.end_time) : null),
        comment: setting.comment,
        operationalEvent: setting.operational_event ? operationalEvents.get(setting.operational_event) : undefined,
      }))

      return (
        <div key={`${index}_${key}`}>
          <DeviationSettingsListItem
            name={key}
            prettyName={targetObjectName}
            componentTagName={componentTagName}
            settings={transformedSettings}
            showButton={showDeviationSettingsLinkButton}
            onEventClick={setEventPostData}
          />
        </div>
      )
    })
  }, [
    digitalTwin?.translations,
    endTime,
    operationalEvents,
    propertiesWithActiveSettingsAndWithoutEntries,
    startTime,
    showDeviationSettingsLinkButton,
    allTagsDict,
  ])

  return (
    <>
      <DeviationSettingsMenuTab
        numberOfSettings={totalDigitalTwinSettingsCount}
        menuWidth={menuWidth}
        onToggle={handleMenuToggle}
      />
      {isMenuOpen && (
        <Box sx={{ width: `${menuWidth}%`, height: `calc(100vh - ${headerHeight}px)` }}>
          <div className={styles.DeviationSettingsMenu} data-testid="deviation-settings-menu">
            {/* e2e: deviation-settings-menu is the panel container selector — do not remove */}
            <div className={styles.DeviationSettingsMenu_Header}>{t('Deviation settings')}</div>
            <div className={styles.DeviationSettingsMenu_Search}>
              <SearchField
                value={searchTerm}
                onChange={(value) => setSearchTerm(String(value))}
                onClear={() => setSearchTerm('')}
                onDarkBackground
              />
            </div>
            {isPendingSettings ? (
              <div>{t('Loading...')}</div>
            ) : activeSettingsAndNoSettings.length === 0 ? (
              <div>{t('No active deviation settings found for this period.')}</div>
            ) : activeSettingsAndNoSettingsMatchingSearchTerm.length === 0 ? (
              <div>{t('No matching deviation settings found.')}</div>
            ) : (
              settingsListItem
            )}
          </div>
        </Box>
      )}
      {eventPostData && (
        <EventPostViewModal closeModal={() => setEventPostData(undefined)} selectedEventPostId={eventPostData?.id} />
      )}
    </>
  )
}
